import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { createIntakeTransfer, listTransferBatchOptions } from '../../api/intakeTransfers';
import { listLookup } from '../../api/masterData';
import { Application } from '../../types/academic';
import { TransferBatchOption } from '../../types/intakeTransfer';
import { transferErrorMessage } from './transferUtils';

const NO_BATCH = 'none';

const batchLabel = (b: TransferBatchOption) =>
  [
    b.batchNumber,
    b.name,
    b.departureDate ? `departs ${new Date(b.departureDate).toLocaleDateString()}` : null,
    b.capacity != null ? `${b.memberCount}/${b.capacity} students` : `${b.memberCount} students`,
  ]
    .filter(Boolean)
    .join(' · ');

/** Counselor → "Request intake/batch transfer" for one application. Nothing
 * changes until a Super Admin approves it. */
export const RequestTransferDialog = ({
  application,
  onClose,
}: {
  application: Application | null;
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const open = !!application;
  const currentBatchId = application?.travelBatchMember?.travelBatch.id ?? null;
  const [toIntakeId, setToIntakeId] = useState<number | ''>('');
  const [toBatch, setToBatch] = useState<string>(NO_BATCH);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!application) return;
    setToIntakeId('');
    setToBatch(currentBatchId ? String(currentBatchId) : NO_BATCH);
    setReason('');
  }, [application, currentBatchId]);

  const intakesQuery = useQuery({
    queryKey: ['lookup', '/master-data/intakes'],
    queryFn: () => listLookup('/master-data/intakes', { activeOnly: true }),
    enabled: open,
  });
  const batchesQuery = useQuery({
    queryKey: ['intake-transfers', 'batch-options'],
    queryFn: listTransferBatchOptions,
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: () =>
      createIntakeTransfer({
        applicationId: application!.id,
        toIntakeId: toIntakeId as number,
        toBatchId: toBatch === NO_BATCH ? null : Number(toBatch),
        reason: reason.trim(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intake-transfers'] });
      onClose();
    },
  });
  const close = () => {
    mutation.reset();
    onClose();
  };

  const batches = batchesQuery.data ?? [];
  const currentBatch = application?.travelBatchMember?.travelBatch ?? null;
  // The current batch may already be full (it counts this student), so it
  // stays selectable; only other full batches are disabled.
  const isFull = (b: TransferBatchOption) =>
    b.id !== currentBatchId && b.capacity != null && b.memberCount >= b.capacity;
  const intakeChanges = toIntakeId !== '' && toIntakeId !== application?.intake?.id;
  const leavesBatch = !!currentBatch && toBatch === NO_BATCH;
  const changesSomething =
    intakeChanges || (toBatch !== NO_BATCH && Number(toBatch) !== currentBatchId);
  const canSubmit =
    toIntakeId !== '' && changesSomething && reason.trim().length >= 5 && !mutation.isPending;

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Request intake / batch transfer</DialogTitle>
      <DialogContent>
        {application && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              {application.applicationNumber} — {application.university.name} /{' '}
              {application.course.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Current intake: <strong>{application.intake?.name ?? 'None'}</strong> · Current batch:{' '}
              <strong>{currentBatch?.batchNumber ?? 'None'}</strong>
            </Typography>
            <Alert severity="info">
              The request stays pending until a Super Admin approves it. Until then the student's
              intake and batch don't change.
            </Alert>
            {mutation.isError && (
              <Alert severity="error">{transferErrorMessage(mutation.error)}</Alert>
            )}
            <TextField
              select
              required
              label="Move to intake"
              value={toIntakeId}
              onChange={(e) => setToIntakeId(Number(e.target.value))}
            >
              {intakesQuery.data?.map((i) => (
                <MenuItem key={i.id as number} value={i.id as number}>
                  {String(i.name)}
                  {i.id === application.intake?.id ? ' (current)' : ''}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Batch after the transfer"
              value={toBatch}
              onChange={(e) => setToBatch(e.target.value)}
              helperText="Batches that have already departed aren't listed."
            >
              <MenuItem value={NO_BATCH}>No batch</MenuItem>
              {batches.map((b) => (
                <MenuItem key={b.id} value={String(b.id)} disabled={isFull(b)}>
                  {batchLabel(b)}
                  {b.id === currentBatchId ? ' (current)' : ''}
                  {isFull(b) ? ' — full' : ''}
                </MenuItem>
              ))}
            </TextField>
            {leavesBatch && (
              <Alert severity="warning">
                On approval the student will be removed from batch {currentBatch!.batchNumber}.
              </Alert>
            )}
            {toIntakeId !== '' && !changesSomething && (
              <Alert severity="warning">Choose a different intake, or a different batch.</Alert>
            )}
            <TextField
              label="Reason"
              required
              multiline
              minRows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. The student asked to defer to the next intake."
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={() => mutation.mutate()}>
          {mutation.isPending ? 'Submitting…' : 'Submit request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
